import { getToken, clearToken } from './auth'

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export class HttpError extends Error {
  status: number
  constructor(status: number, msg: string) {
    super(`${status} ${msg}`)
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(BASE + path, { ...init, headers })
  if (res.status === 401) {
    clearToken()
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('auth-cleared'))
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new HttpError(res.status, text.slice(0, 200))
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const apiGet = <T>(p: string) => request<T>(p)
export const apiPost = <T>(p: string, body?: unknown) =>
  request<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const apiPatch = <T>(p: string, body: unknown) =>
  request<T>(p, { method: 'PATCH', body: JSON.stringify(body) })
export const apiPut = <T>(p: string, body: unknown) =>
  request<T>(p, { method: 'PUT', body: JSON.stringify(body) })
export const apiDelete = <T>(p: string) =>
  request<T>(p, { method: 'DELETE' })

// ---- types

export interface SessionRow {
  id: string
  tool: 'claude_code' | 'codex' | string
  project_path: string
  project_label: string
  last_activity: number
  current_summary: string | null
  dismissed_at?: number | null
}

export interface TaskRow {
  id: string
  repo_label: string
  file_path: string
  task_file: string
  line_number: number
  title: string
  status: 'open' | 'done'
  detected_at: number
  closed_at: number | null
}

export interface FocusItem {
  id: string
  title: string
  reason: string
  source: 'session' | 'task' | 'email'
}

export interface FocusResponse {
  generated_at: number
  model: string
  top: FocusItem[]
  runner_up: FocusItem[]
  error?: string
  debounced?: boolean
}

export interface InboxRow {
  id: string
  from_addr: string
  subject: string
  received_at: number
  flagged: number
  is_priority: number
  account?: string
  ai_urgency?: number | null
  ai_reason?: string | null
  snippet?: string | null
}

export interface InboxResponse {
  hot?: InboxRow[]
  priority: InboxRow[]
  flagged: InboxRow[]
  recent?: InboxRow[]
  total_unread: number
}

export interface CalendarEventRow {
  id: string
  start_at: number
  end_at: number
  title: string
  location: string | null
  is_focus_block: number
  account?: string
  ai_urgency?: number | null
  ai_reason?: string | null
}

export interface CalendarResponse {
  events: CalendarEventRow[]
  focus_block_minutes: number
}

export interface CutoffSettings {
  weekday: string
  weekend: string
}

export interface DateRow {
  id: number
  label: string
  date_iso: string
  is_annual: number
  notes: string | null
  days_until: number
}

export interface MentionRow {
  id: number
  note: string
  captured_at: number
}

export interface GiftRow {
  id: number
  idea: string
  budget_pence: number | null
  occasion: string | null
  status: 'idea' | 'bought' | 'given' | string
  created_at: number
  due_by: string | null
}

export interface SmallActDue {
  id: number
  act: string
  last_done: number | null
  cadence_days: number
  days_overdue: number
}

export interface TogetherRow {
  id: number
  kind: string
  occurred_on: string
  note: string | null
  days_ago: number
}

export interface WeekendPlan {
  weekend_of: string
  plan_md: string
  updated_at: number
}

export interface HerWeekEvent {
  start_at: number
  end_at: number
  title: string
}

export interface HomeOverview {
  next_dates: DateRow[]
  open_mentions: MentionRow[]
  gift_ideas: GiftRow[]
  small_acts_due: SmallActDue[]
  last_together: (TogetherRow) | null
  weekend_plan: WeekendPlan
  her_week_ahead: HerWeekEvent[]
}
